import React, { useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { Analytics, CallMetricsEventEnum, ConferenceTypeEnum, ConnectionQualityEnum, UserTypeEnum } from '@matap/shared';
import styled from 'styled-components/native';
import { useUser } from '@/hooks/use.user';
import { Box } from '@/components/base/box';
import { AppTextView } from '@/components/base/app.text.view';
import { AppAvatar } from '../app.avatar';
import { AnswerButtonsView } from './answer.buttons.view';
import { CameraView } from './camera.view';
import { RemoteView } from '@/components/compose/call/rtc-view/remote.view';
import { CallButtonsView } from './call.buttons.view';
import { CallStateView } from './call.state.view';
import { useSocket } from '@/hooks/use.socket';
import { hpVal } from '../../../utils';
import { P2PPeer, StreamState } from '@/webrtc/p2p.peer';
import { P2PRoom } from '@/webrtc/p2p.room';

function BaseTransmittingView({ connection }: { connection: P2PRoom }) {
  const { userState, userType } = useUser();
  const imageUrl = userType === UserTypeEnum.PATIENT ? userState.visit.doctor.imageFile?.metadata.url : userState.visit.patient.imageFile?.metadata.url;
  const name = userType === UserTypeEnum.PATIENT ? userState.visit.doctor.name : userState.visit.patient.name;

  const [speakerOn, setSpeakerOn] = useState(connection.getDeviceManager().getSelectedSpeakerDevice() === 'speaker');
  const [localStream, setLocalStream] = useState<MediaStream>();
  const [remotePeer, setRemotePeer] = useState<P2PPeer>();
  const [muted, setMuted] = useState(false);
  const [state, setState] = useState<StreamState>('connecting');
  const [quality, setQuality] = useState<ConnectionQualityEnum>();
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const { sendAnalytic, hangupCall } = useSocket();

  useEffect(() => {
    const localStreamListener = () => {
      if (connection.type === ConferenceTypeEnum.video) {
        setLocalStream(connection.getPeerLocalStream().stream());
      }
      setMuted(connection.getPeerLocalStream().isMuted());
    };
    const peersListener = () => setRemotePeer(connection.getPeers()[0]);
    const speakerListener = () => setSpeakerOn(connection.getDeviceManager().getSelectedSpeakerDevice() === 'speaker');

    connection.getPeerLocalStream().on('change', localStreamListener);
    connection.getDeviceManager().on('change', speakerListener);
    connection.on('peers', peersListener);

    peersListener();
    localStreamListener();

    return () => {
      connection.getDeviceManager().off('change', speakerListener);
      connection.off('peers', peersListener);
      connection.getPeerLocalStream().off('change', localStreamListener);
    };
  }, [connection]);

  useEffect(() => {
    if (!remotePeer) return;
    const listener = () => setState(remotePeer.getState());
    remotePeer.on('change', listener);
    listener();
    return () => {
      remotePeer.off('change', listener);
    };
  }, [remotePeer]);

  useEffect(() => {
    if (!remotePeer) return;
    const listener = (quality: ConnectionQualityEnum) => setQuality(quality);
    remotePeer.on('quality-change', listener);
    return () => {
      remotePeer.off('quality-change', listener);
    };
  }, [remotePeer]);

  useEffect(() => {
    if (state === 'connected') {
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now();
      }
      const interval = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      startTimeRef.current = null;
      setElapsed(0);
    }
  }, [state]);

  return (
    <TransmittingContainer>
      <Box flex={1}>
        {remotePeer && <Remote connection={connection} />}
        {localStream && connection.type === ConferenceTypeEnum.video && <Camera deviceManager={connection.getDeviceManager()} localStream={localStream} />}
        {connection.type === ConferenceTypeEnum.audio && (
          <>
            <TopColorContainer />
            <Container>
              <InnerContainer>
                <UserImage url={imageUrl} />
                <NameView bold variant={'giant'} textColor={'blue'} text={name} />
                <Box flexGrow={1} alignItems={'center'} justifyContent={'center'}>
                  {elapsed > 0 && <TimerText text={formatDuration(elapsed)} variant={'medium'} textColor={'blue'} />}
                </Box>
              </InnerContainer>
            </Container>
          </>
        )}
        {quality && <QualityDot quality={quality} />}
      </Box>
      <AnswerButtonsView
        state={'transmitting'}
        onReject={() => {
          sendAnalytic(new Analytics.AbstractCallMetric(connection._id, CallMetricsEventEnum.END_CALL_CLICKED));
          hangupCall(connection._id);
          connection.destroy();
        }}
      />

      {state !== 'connected' && <CallStateView text={'در حال اتصال'} />}
      <CallButtonsView
        connection={connection}
        onSwitchCamera={
          connection.type === ConferenceTypeEnum.video && Platform.OS !== 'web'
            ? () => {
                sendAnalytic(new Analytics.AbstractCallMetric(connection._id, CallMetricsEventEnum.CAMERA_SWITCH_CLICKED));
                connection.getPeerLocalStream().switchCamera();
              }
            : undefined
        }
        speakerOn={speakerOn}
        muted={muted}
        onMute={() => {
          sendAnalytic(new Analytics.AbstractCallMetric(connection._id, CallMetricsEventEnum.MUTE_CLICKED));
          connection.getPeerLocalStream().toggleMute();
        }}
        onSpeaker={
          Platform.OS !== 'web'
            ? () => {
                sendAnalytic(new Analytics.AbstractCallMetric(connection._id, CallMetricsEventEnum.SPEAKER_CLICKED));
                connection.getDeviceManager().setSpeakerDevice(!speakerOn);
              }
            : undefined
        }
      />
    </TransmittingContainer>
  );
}

const Remote = styled(RemoteView)`
  width: 100%;
  height: 100%;
  position: absolute;
  border-color: red;
`;

const Camera = styled(CameraView)`
  position: absolute;
  width: 100px;
  height: 150px;
  top: ${({ theme }) => theme.hp(3)}px;
  right: ${({ theme }) => theme.hp(1)}px;
  border-radius: 5px;
`;

const Container = styled(Box)`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const TopColorContainer = styled(Box)`
  height: ${hpVal(22)};
  width: 100%;
  top: 0;
  background-color: ${({ theme }) => theme.colors.jade};
`;

const InnerContainer = styled(Box)`
  flex-grow: 1;
  align-items: center;
`;

const UserImage = styled(AppAvatar)`
  width: ${({ theme }) => theme.hp(15)}px;
  height: ${({ theme }) => theme.hp(15)}px;
  margin-top: ${({ theme }) => theme.hp(-(15 / 2))}px;
`;

const NameView = styled(AppTextView)`
  text-align: center;
  margin-top: ${({ theme }) => theme.hp(5)}px;
  margin-horizontal: 7px;
`;

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const TimerText = styled(AppTextView)`
  font-variant: tabular-nums;
  letter-spacing: 3px;
`;

const qualityColors: Record<ConnectionQualityEnum, string> = {
  [ConnectionQualityEnum.excellent]: '#4CAF50',
  [ConnectionQualityEnum.fair]: '#FFC107',
  [ConnectionQualityEnum.poor]: '#FF9800',
  [ConnectionQualityEnum.bad]: '#F44336',
};

const QualityDot = styled(View)<{ quality: ConnectionQualityEnum }>`
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: ${({ quality }) => qualityColors[quality]};
  position: absolute;
  top: ${({ theme }) => theme.hp(1)}px;
  left: ${({ theme }) => theme.hp(1)}px;
`;

const TransmittingContainer = styled(Box)`
  flex: 1;
  flex-direction: column;
`;

export const CallTransmittingView = React.memo(BaseTransmittingView);
